/**
 * Single source of truth for "does this path exist somewhere in the repo?".
 *
 * Replaces three divergent implementations previously scattered across:
 *   - path.ts:pathExistsUncached
 *   - broken-link.ts:linkTargetExists
 *   - edges.ts:checkEdges (inline resolution)
 *
 * Each call site declares which resolution layers it cares about via `opts`.
 * Adding a new layer (e.g. workspace alias, monorepo package) is one place.
 */
import { existsSync, readFileSync } from "node:fs";
import { join, resolve } from "node:path";
import { globSync } from "glob";

export interface DualRootContext {
  projectRoot: string;
  scaffoldRoot: string;
}

export interface ResolveOptions {
  /** Try the directory containing the file that referenced the target. */
  fromFileDir?: string;
  /** Try stripping the `.cai/` or `.context-condensing/` prefix. */
  tryStripScaffoldPrefix?: boolean;
  /** Try resolving TypeScript/bundler path aliases (`@/`, `@components/`, `~/`). */
  tryTsConfigAliases?: boolean;
  /** Try fuzzy matching against the project file index. */
  tryFuzzyMatch?: boolean;
}

const SCAFFOLD_PREFIXES = [".cai/", ".context-condensing/"] as const;

/** Returns true iff `target` resolves to an existing file via any of the enabled layers. */
export function resolveDualRootPath(
  target: string,
  ctx: DualRootContext,
  opts: ResolveOptions = {}
): boolean {
  if (!target) return false;

  // Layer 1: relative to the referencing file (if provided)
  if (opts.fromFileDir && existsSync(resolve(opts.fromFileDir, target))) return true;

  // Layer 2: project root
  if (existsSync(resolve(ctx.projectRoot, target))) return true;

  // Layer 3: scaffold root (when distinct from project root)
  if (ctx.scaffoldRoot !== ctx.projectRoot) {
    if (existsSync(resolve(ctx.scaffoldRoot, target))) return true;
  }

  // Layer 4: strip a scaffold-directory prefix and try project root again
  if (opts.tryStripScaffoldPrefix) {
    for (const prefix of SCAFFOLD_PREFIXES) {
      if (target.startsWith(prefix)) {
        const without = target.slice(prefix.length);
        if (existsSync(resolve(ctx.projectRoot, without))) return true;
      }
    }
  }

  // Layer 5: TypeScript path alias resolution
  if (opts.tryTsConfigAliases && (target.startsWith("@") || target.startsWith("~/"))) {
    if (resolveAlias(target, ctx.projectRoot)) return true;
  }

  // Layer 6: fuzzy match against the file index
  if (opts.tryFuzzyMatch) {
    const fileName = target.split("/").pop();
    if (fileName) {
      const index = getFileIndex(ctx.projectRoot);
      const candidates = index.get(fileName);
      if (candidates?.some((c) => c === target || c.endsWith("/" + target))) return true;
    }
  }

  return false;
}

// ── Lazy alias resolution ───────────────────────────────────────────────────

const tsConfigCache = new Map<string, Array<{ prefix: string; targets: string[] }> | null>();

function loadTsPaths(projectRoot: string): Array<{ prefix: string; targets: string[] }> | null {
  if (tsConfigCache.has(projectRoot)) return tsConfigCache.get(projectRoot)!;

  const tsconfigPath = resolve(projectRoot, "tsconfig.json");
  if (!existsSync(tsconfigPath)) {
    tsConfigCache.set(projectRoot, null);
    return null;
  }

  try {
    // Strip single-line comments and trailing commas for lenient JSON parsing
    const raw = readFileSync(tsconfigPath, "utf-8")
      .replace(/\/\/.*$/gm, "")
      .replace(/,\s*([\]}])/g, "$1");
    const tsconfig = JSON.parse(raw);
    const paths: Record<string, string[]> | undefined =
      tsconfig?.compilerOptions?.paths;

    if (!paths || Object.keys(paths).length === 0) {
      tsConfigCache.set(projectRoot, null);
      return null;
    }

    const mappings = Object.entries(paths).map(([pattern, targets]) => ({
      prefix: pattern.replace(/\*$/, ""),
      targets: targets.map((t) => t.replace(/\*$/, "")),
    }));
    tsConfigCache.set(projectRoot, mappings);
    return mappings;
  } catch {
    tsConfigCache.set(projectRoot, null);
    return null;
  }
}

function resolveAlias(value: string, projectRoot: string): boolean {
  // ── tsconfig.json paths ──
  const mappings = loadTsPaths(projectRoot);
  if (mappings) {
    for (const { prefix, targets } of mappings) {
      if (value.startsWith(prefix)) {
        const rest = value.slice(prefix.length);
        for (const target of targets) {
          if (existsSync(resolve(projectRoot, target + rest))) return true;
        }
      }
    }
  }

  // ── ~ alias (Nuxt/Vite convention: ~ → project root) ──
  if (value.startsWith("~/")) {
    const stripped = value.slice(2);
    if (existsSync(resolve(projectRoot, stripped))) return true;
    // Common convention: ~/components → src/components
    if (existsSync(resolve(projectRoot, "src", stripped))) return true;
  }

  return false;
}

/** Test-only: clear the tsconfig cache. */
export function _clearTsConfigCache(): void {
  tsConfigCache.clear();
}

// ── Lazy file index ─────────────────────────────────────────────────────────

let fileIndexProject: string | null = null;
let fileIndex: Map<string, string[]> | null = null;

function getFileIndex(projectRoot: string): Map<string, string[]> {
  if (fileIndex && fileIndexProject === projectRoot) return fileIndex;
  fileIndex = new Map();
  fileIndexProject = projectRoot;
  const allFiles = globSync("**/*", {
    cwd: projectRoot,
    ignore: ["node_modules/**", ".cai/**", ".context-condensing/**", "dist/**", ".git/**", "build/**"],
    maxDepth: 6,
    nodir: true,
  });
  for (const f of allFiles) {
    const name = f.split("/").pop()!;
    if (!fileIndex.has(name)) fileIndex.set(name, []);
    fileIndex.get(name)!.push(f);
  }
  return fileIndex;
}

/** Test-only: clear the file index cache. */
export function _clearFileIndexCache(): void {
  fileIndexProject = null;
  fileIndex = null;
}

// ── .gitignore matching ─────────────────────────────────────────────────────

/** Minimal gitignore pattern matcher — covers the common cases without a dep. */
function buildGitignoreMatcher(projectRoot: string): ((path: string) => boolean) | null {
  const gitignorePath = join(projectRoot, ".gitignore");
  if (!existsSync(gitignorePath)) return null;
  let raw: string;
  try { raw = readFileSync(gitignorePath, "utf-8"); } catch { return null; }
  const patterns = raw
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"));
  return (filePath: string): boolean => {
    const normalized = filePath.replace(/\\/g, "/").replace(/^\//, "");
    for (const pattern of patterns) {
      const p = pattern.replace(/^\//, "");
      if (normalized === p || normalized.startsWith(p + "/")) return true;
      if (!p.includes("/")) {
        const basename = normalized.split("/").pop() ?? "";
        if (basename === p) return true;
        if (p.startsWith("*") && basename.endsWith(p.slice(1))) return true;
      }
      if (p.startsWith("**/")) {
        const suffix = p.slice(3);
        if (normalized === suffix || normalized.endsWith("/" + suffix)) return true;
      }
    }
    return false;
  };
}

const gitignoreMatcherCache = new Map<string, ((path: string) => boolean) | null>();

/** Returns true if the path matches a .gitignore rule in projectRoot. */
export function isPathGitignored(filePath: string, projectRoot: string): boolean {
  let matcher = gitignoreMatcherCache.get(projectRoot);
  if (matcher === undefined) {
    matcher = buildGitignoreMatcher(projectRoot);
    gitignoreMatcherCache.set(projectRoot, matcher);
  }
  return matcher?.(filePath) ?? false;
}

/** Test-only: clear the gitignore cache. */
export function _clearGitignoreCache(): void {
  gitignoreMatcherCache.clear();
}

/** Test-only: clear every cache this module owns. */
export function _clearAllResolveCaches(): void {
  _clearTsConfigCache();
  _clearFileIndexCache();
  _clearGitignoreCache();
}